import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  deleteContacts,
  deleteContactsUserPortal,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { ContactGroup, Person } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteContactsProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  userView?: boolean;
  contactList: Person[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  selectedLabel: string;
  selectedRows: string[];
  groupMembers: string[];
  labelList: ContactGroup[];
  page: number;
  pageSize: number;
  changeLogRoles: string[];
}

export default function DeleteContactsDialog({
  open,
  handleClose,
  userEmail,
  userView,
  contactList,
  selectedLabel,
  selectedRows,
  setSelectedRows,
  groupMembers,
  labelList,
  page,
  pageSize,
  changeLogRoles,
}: DeleteContactsProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteContacts(userEmail, selectedRows, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to delete contacts has begun.',
      });

      const newList = contactList.filter(
        (group) => !selectedRows.includes(group.resourceName || '')
      );

      const newGroupMembers = groupMembers.filter(
        (member) => !selectedRows.includes(member)
      );

      labelList.forEach((label) => {
        if (label.resourceName === selectedLabel) {
          queryClient.setQueryData(
            [UserKeys.ContactGroup, userEmail, selectedLabel],
            newGroupMembers
          );
          queryClient.setQueryData(
            [
              UserKeys.Contacts,
              userEmail,
              selectedLabel,
              newGroupMembers,
              page,
              pageSize,
            ],
            newList
          );
        } else {
          queryClient.invalidateQueries({
            queryKey: [UserKeys.Labels, userEmail],
          });
          queryClient.invalidateQueries({
            queryKey: [UserKeys.ContactGroup, userEmail],
          });
          queryClient.invalidateQueries({
            queryKey: [UserKeys.Contacts, userEmail],
          });
        }
      });
      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting contacts. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.personal_contacts
          .delete_personal_contacts,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.personalContacts,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: () => deleteContactsUserPortal(selectedRows),
      onSuccess: () => {
        setSnackbar({
          open: true,
          severity: 'info',
          message: 'The task to delete contacts has begun.',
        });

        const newList = contactList.filter(
          (group) => !selectedRows.includes(group.resourceName || '')
        );

        const newGroupMembers = groupMembers.filter(
          (member) => !selectedRows.includes(member)
        );

        labelList.forEach((label) => {
          if (label.resourceName === selectedLabel) {
            queryClient.setQueryData(
              [UserKeys.ContactGroup, userEmail, selectedLabel],
              newGroupMembers
            );
            queryClient.setQueryData(
              [
                UserKeys.Contacts,
                userEmail,
                selectedLabel,
                newGroupMembers,
                page,
                pageSize,
              ],
              newList
            );
          } else {
            queryClient.invalidateQueries({
              queryKey: [UserKeys.Labels, userEmail],
            });
            queryClient.invalidateQueries({
              queryKey: [UserKeys.ContactGroup, userEmail],
            });
            queryClient.invalidateQueries({
              queryKey: [UserKeys.Contacts, userEmail],
            });
          }
        });
        setSelectedRows([]);
        setChangeLogMessage('');
        handleClose();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue deleting contacts. Please refresh and try again.',
        });
      },
    });

  function handleSubmit() {
    if (
      !userView &&
      isAbleToSeeUIElement(changeLogRoles, UserRoles.RemoveContacts.value)
    ) {
      setOpenChangeLogDialog(true);
    } else if (userView) {
      userPortalMutate();
    } else {
      mutate();
    }
  }

  let contactName;
  if (selectedRows.length === 1) {
    const contact = contactList.find(
      (contact) => contact.resourceName === selectedRows[0]
    )!;
    contactName =
      contact.names && contact.names[0] && contact.names[0].displayName
        ? contact.names[0].displayName
        : contact.emailAddresses &&
          contact.emailAddresses[0] &&
          contact.emailAddresses[0].value
        ? contact.emailAddresses[0].value
        : contact.resourceName;
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={selectedRows.length > 1 ? 'Delete Contacts' : 'Delete Contact'}
      dialogContent={
        <Box>
          <Typography sx={{ mb: 2 }}>
            {selectedRows.length > 1 ? (
              <>
                Delete <strong>{selectedRows.length}</strong> contacts from{' '}
                <strong>{userEmail}'s</strong> personal contacts?
              </>
            ) : (
              <>
                Delete <strong>{contactName}</strong> from{' '}
                <strong>{userEmail}'s</strong> personal contacts?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: selectedRows.length > 1 ? 'Delete Contacts' : 'Delete Contact',
        action: handleSubmit,
        loading: isPending || userPortalPending,
        disabled: false,
        color: 'error',
      }}
    />
  );
}
